import { List, Map } from 'immutable';
import memoizee from 'memoizee';
import { createSelector } from 'reselect';
import { createEmbedPreviewUrl } from 'redux/middleware/api/embed-service';
import { NormalizedVideoUpload } from 'redux/middleware/api/media-upload-service';
import { tokenSelector } from 'redux/modules/auth/selectors';
import {
  getVideoLayoutTimelineRevisionIdSelector,
  mediaSegmentsByIdSelector as mediaSegmentsByIdEntitySelector,
  videoSelector,
} from 'redux/modules/entities';
import { State } from 'redux/types';
import { TrackType } from 'types';
import { createAudioClips } from 'utils/embed/audio-clips';
import { isConfigEmpty } from 'utils/embed/embed';
import { removeGapsFromTime } from 'utils/embed/media-segment';
import { Pixels } from 'utils/measurement';
import {
  EmbedLayoutSegment,
  EmbedVideoLayoutInfoById,
  LastLayoutActionMeta,
} from '../types';
import {
  captionsStyleSelector,
  embedSlideIdsSelector,
  tracksByIdSelector,
  videosByIdSelector,
} from './common';
import {
  captionsVideoSourceSelector,
  embedAudioSelector,
  embedConfigSelector,
  embedLoadingSelector,
  videoIdsSelector,
} from './legacy-selectors';

export const embedUrlSelector = createSelector(
  [embedConfigSelector, tokenSelector, embedLoadingSelector],
  (config, token, isLoading) =>
    isConfigEmpty(config) || isLoading
      ? undefined
      : createEmbedPreviewUrl({
          widgetId: config.wid,
          recordingId: config.embedConfig.recordingId,
          token,
        }),
);

export const activeEditOperationSelector = (state: State) =>
  state.getIn(['embed', 'activeEditOperation']);

export const isAssetEditingSelector = createSelector(
  activeEditOperationSelector,
  activeEditOperation => activeEditOperation !== undefined,
);

export const trackTypeSelector = (trackId: string) =>
  createSelector(
    tracksByIdSelector,
    tracksById => tracksById?.getIn([trackId, 'type']) as TrackType,
  );

export const mediaSegmentsByIdSelector = mediaSegmentsByIdEntitySelector;

export const mediaSegmentsSelector = createSelector(
  mediaSegmentsByIdSelector,
  (_, id: string) => id,
  (mediaSegmentsById, id) => {
    if (!mediaSegmentsById || !id) {
      return [];
    }

    return mediaSegmentsById[id];
  },
);

export const audioClipsSelector = createSelector(
  [
    videosByIdSelector,
    videoIdsSelector,
    embedAudioSelector,
    mediaSegmentsByIdSelector,
  ],
  (videosById, videoIds, audio, mediaSegmentsById) =>
    createAudioClips(
      audio,
      {
        order: videoIds,
        data: videosById,
      },
      mediaSegmentsById,
    ),
);

export const formatStateForEmbedConfigDependenciesSelector = createSelector(
  mediaSegmentsByIdSelector,
  videosByIdSelector,
  (mediaSegmentData, videosById) => ({
    mediaSegmentData,
    videosById,
  }),
);

const slideIdsSelector = createSelector(
  // FIXME embedSlideIds in redux should be an immutable list, not an array
  embedSlideIdsSelector,
  (ids = []) => new Set(ids),
);

const gifIdsSelector = createSelector(videosByIdSelector, videos => {
  const gifIds = videos
    .filter(v => v.assetType === 'gif')
    .keySeq()
    .toJS();
  return new Set(gifIds);
});

const autoframedVideoIdsSelector = createSelector(
  videosByIdSelector,
  videos => {
    const ids = videos
      .filter(v => v.assetType === 'video' && v.layoutInfoId !== undefined)
      .keySeq()
      .toJS();
    return new Set(ids);
  },
);

export const getTrackIdsSelector = memoizee((trackId: string) =>
  createSelector([tracksByIdSelector, () => trackId], (tracks, id) =>
    tracks.getIn([id, 'data'], List()).toJS(),
  ),
);

export const getTrackAssetsSelector = memoizee((trackId: string) =>
  createSelector(
    [
      getTrackIdsSelector(trackId),
      slideIdsSelector,
      gifIdsSelector,
      autoframedVideoIdsSelector,
    ],
    (assetIds, slideIds, gifIds, autoframedVideoIds) =>
      assetIds.map(id => {
        if (slideIds.has(id)) {
          return { id, type: 'image' };
        }

        if (gifIds.has(id)) {
          return { id, type: 'gif-video' };
        }

        if (autoframedVideoIds.has(id)) {
          return { id, type: 'autoframed-video' };
        }

        return { id, type: 'video' };
      }),
  ),
);

export const getVideoSelector = memoizee((videoId: string) =>
  createSelector([videosByIdSelector], videosById =>
    !videosById || !videoId ? undefined : videosById.get(videoId),
  ),
);

export const createIsVideoAutoframedSelector = (videoId: string) => (
  state: State,
) => {
  const ids = autoframedVideoIdsSelector(state);
  return ids.has(videoId);
};

export const isCaptionsSourceVideoAutoframedSelector = (state: State) => {
  const captionsSource = captionsVideoSourceSelector(state);
  const videoId = captionsSource?.get('id');
  return !videoId ? false : createIsVideoAutoframedSelector(videoId)(state);
};

const videoLayoutInfoByIdSelector = (state: State): EmbedVideoLayoutInfoById =>
  state.getIn(['embed', 'videoLayoutInfoById'], Map());

export const createVideoLayoutIdSelector = (videoId: string) => (
  state: State,
) => {
  const video = getVideoSelector(videoId)(state);
  if (!video) {
    return undefined;
  }

  return video.layoutInfoId;
};

const getRawVideoLayoutInfoSelector = memoizee((videoId: string) =>
  createSelector(
    [createVideoLayoutIdSelector(videoId), videoLayoutInfoByIdSelector],
    (layoutId, layoutInfoById) => {
      if (!layoutId) {
        return undefined;
      }

      const layoutInfo = layoutInfoById.get(layoutId);

      if (!layoutInfo) {
        return undefined;
      }

      return layoutInfo.get('segments').toJS() as EmbedLayoutSegment[];
    },
  ),
);

const createVideoMediaSegmentInfoIdSelector = (videoId: string) => (
  state: State,
) => {
  const video = getVideoSelector(videoId)(state);
  if (!video) {
    return undefined;
  }

  return video.mediaSegmentInfoId;
};

export const getVideoMediaSegmentsSelector = memoizee((videoId: string) =>
  createSelector(
    [createVideoMediaSegmentInfoIdSelector(videoId), mediaSegmentsByIdSelector],
    (mediaSegmentInfoId, mediaSegmentsById) => {
      if (!mediaSegmentInfoId || !mediaSegmentsById) {
        return undefined;
      }

      return mediaSegmentsById[mediaSegmentInfoId];
    },
  ),
);

export const getVideoLayoutInfoSelector = memoizee((videoId: string) => {
  return createSelector(
    [
      getRawVideoLayoutInfoSelector(videoId),
      getVideoMediaSegmentsSelector(videoId),
    ],
    (rawVideoLayoutInfo, videoMediaSegments) => {
      if (!rawVideoLayoutInfo && !videoMediaSegments) {
        return undefined;
      }

      if (!videoMediaSegments) {
        return rawVideoLayoutInfo;
      }

      return rawVideoLayoutInfo.map(segment => {
        const adjustedStartMillis = removeGapsFromTime(
          segment.playOffsetMillis,
          videoMediaSegments,
        );

        const adjustedEndMillis = removeGapsFromTime(
          segment.playOffsetMillis + segment.durationMillis,
          videoMediaSegments,
        );

        return {
          ...segment,
          playOffsetMillis: adjustedStartMillis,
          durationMillis: adjustedEndMillis - adjustedStartMillis,
        };
      });
    },
  );
});

export const createLayoutSegmentCountSelector = (videoId: string) => (
  state: State,
) => {
  const layoutTimeline = getVideoLayoutInfoSelector(videoId)(state);
  return layoutTimeline?.length;
};

export const createLayoutSegmentByIdSelector = (
  videoId: string,
  segmentId: string,
) =>
  createSelector([getVideoLayoutInfoSelector(videoId)], layoutTimeline =>
    layoutTimeline?.find(segment => segment.segmentId === segmentId),
  );

export const createLayoutSegmentSlotsSelector = (
  videoId: string,
  segmentId: string,
) => (state: State) =>
  createLayoutSegmentByIdSelector(videoId, segmentId)(state)?.slots;

export const createLayoutSegmentSlotCountSelector = (
  videoId: string,
  segmentId: string,
) => (state: State) =>
  createLayoutSegmentSlotsSelector(videoId, segmentId)(state)?.length;

export const createLayoutSegmentSubjectsSelector = (
  videoId: string,
  segmentId: string,
) => (state: State) =>
  createLayoutSegmentByIdSelector(videoId, segmentId)(state)?.subjects;

export const createLayoutSegmentBackfillSelector = (
  videoId: string,
  segmentId: string,
) => (state: State) =>
  createLayoutSegmentByIdSelector(videoId, segmentId)(state)?.backfill;

export const createLayoutSegmentCaptionOverrideSelector = (
  videoId: string,
  segmentId: string,
) => (state: State) =>
  createLayoutSegmentByIdSelector(videoId, segmentId)(state)?.caption;

export const createLayoutSegmentCaptionsPositionOverrideSelector = (
  videoId: string,
  segmentId: string,
) => (state: State) =>
  createLayoutSegmentCaptionOverrideSelector(videoId, segmentId)(state)?.region
    ?.properties;

export const dirtyLayoutIdsSelector = (state: State) => {
  const videoLayoutInfoById = videoLayoutInfoByIdSelector(state);

  return videoLayoutInfoById
    .filter(
      layoutInfo =>
        layoutInfo.get('lastSavedRevisionId') < layoutInfo.get('revisionId'),
    )
    .map(layoutInfo => layoutInfo.get('id'))
    .toArray();
};

export const createLayoutTimelineSelector = (layoutId: string) => (
  state: State,
) => {
  const revisionId = getVideoLayoutTimelineRevisionIdSelector(layoutId)(state);
  const videoLayoutInfoById = videoLayoutInfoByIdSelector(state);
  const videoLayoutInfo = videoLayoutInfoById.get(layoutId);

  return {
    uuid: layoutId,
    revisionId,
    data: videoLayoutInfo.get('segments').toJS(),
  };
};

const lastLayoutActionMetaSelector = (state: State) =>
  state.getIn(['embed', 'lastLayoutActionMeta']);

const lastSplitLayoutActionMetaSelector = (state: State) => {
  const meta = lastLayoutActionMetaSelector(state);

  return !meta || meta.get('type') !== 'split'
    ? undefined
    : (meta.toJS() as LastLayoutActionMeta);
};

export const lastSplitLayoutErrorMessageSelector = (state: State) => {
  const meta = lastSplitLayoutActionMetaSelector(state);

  return meta?.errorMessage;
};

export const getVideoUploadSelector = memoizee(
  (videoId: string) => (state: State) => {
    const embedVideo = getVideoSelector(videoId)(state);

    return videoSelector(
      state,
      embedVideo?.serverId,
    )?.toJS() as NormalizedVideoUpload;
  },
);

export const videoDimensionsSelector = (videoId: string) => (state: State) => {
  const video = getVideoUploadSelector(videoId)(state);

  return !video
    ? undefined
    : {
        height: video.videoHeight,
        width: video.videoWidth,
      };
};

export const captionsSizeSelector = createSelector(
  captionsStyleSelector,
  style => {
    if (!style) {
      return undefined;
    }

    const viewport = style.get('viewport')?.toJS();
    const height = style.getIn(['size', 'height']);
    const width = style.getIn(['size', 'width']);

    if (!viewport) {
      return undefined;
    }

    return {
      height: new Pixels(height).toUnit('vh', viewport),
      width: new Pixels(width).toUnit('vw', viewport),
    };
  },
);
